import * as React from 'react';
import { queryNoTracking, update } from '../../../../services/DataService';
import { format, t } from '../../../../services/HelperService';
import { AccentPopup } from '../../../../dialogs/AccentPopup';
import { AccentSpinner } from '../../../AccentSpinner';

const fileNotesPageSize = 3;
const q_Grid_FileNotes = queryNoTracking('Grid_FileNotes');

const chipTones = {
  green: { background: '#e8f5e9', color: '#2e7d32' },
  blue: { background: '#e3f2fd', color: '#1565c0' },
  amber: { background: '#fff3cd', color: '#9a6700' },
  red: { background: '#fdecea', color: '#b3261e' },
  gray: { background: '#f1f3f5', color: '#495057' },
  purple: { background: '#f3e8fd', color: '#6f42c1' },
};

const avatarColors = ['#1565c0', '#2e7d32', '#9a6700', '#6f42c1', '#b3261e', '#0d7c8c'];

export const translateKey = (value) => {
  const safe = (value ?? '').toString().trim();
  return safe ? t(safe) : '';
};

export const money = (value, emptyIfZero) => format.formatCurrency(value ?? 0, emptyIfZero);

export const shortDate = (value) => {
  if (!value) return '';
  return format.formatDate(value);
};

export const shortTime = (value) => {
  if (!value) return '';
  return format.formatTime(value);
};

export const isPastDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
};

export const getInitials = (name) => {
  const parts = (name ?? '').toString().trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts.slice(0, 2).map(p => p[0].toUpperCase()).join('');
};

export const Chip = ({ tone = 'gray', title, children }) => (
  <span
    title={title}
    style={{
      display: 'inline-block',
      fontSize: 10,
      fontWeight: 500,
      padding: '2px 7px',
      borderRadius: 10,
      whiteSpace: 'nowrap',
      maxWidth: 160,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle',
      ...(chipTones[tone] ?? chipTones.gray),
    }}
  >
    {children}
  </span>
);

export const ChipRow = ({ children, style }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, ...style }}>
    {children}
  </div>
);

export const MetaRow = ({ icon, children, right, danger }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 12, color: danger ? '#b3261e' : '#495057' }}>
    {icon && <span className="material-icons" style={{ fontSize: 13, color: danger ? '#b3261e' : '#868e96' }}>{icon}</span>}
    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
    {right != null && <span style={{ fontWeight: 600, color: danger ? '#b3261e' : '#212529' }}>{right}</span>}
  </div>
);

export const CardHeader = ({ title, subTitle, repName }) => {
  const initials = getInitials(repName);
  const avatarColor = avatarColors[Math.abs((repName ?? '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % avatarColors.length];

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, paddingRight: 48 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#212529', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </div>
        {subTitle}
      </div>
      {initials && (
        <span
          title={repName}
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            background: avatarColor,
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {initials}
        </span>
      )}
    </div>
  );
};

export const ReferenceRow = ({ reference, linked }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
    {reference && <span style={{ fontSize: 12, fontWeight: 600, color: '#343a40' }}>{reference}</span>}
    {linked && <Chip tone="gray"># {linked}</Chip>}
  </div>
);

export const ProgressBar = ({ ratio }) => {
  const safeRatio = Math.max(0, Math.min(1, ratio ?? 0));
  return (
    <div style={{ marginTop: 5, height: 4, borderRadius: 2, background: '#e9ecef', overflow: 'hidden' }}>
      <div style={{ width: `${safeRatio * 100}%`, height: '100%', background: safeRatio >= 1 ? '#2e7d32' : '#c98a00' }} />
    </div>
  );
};

const noteInputStyle = {
  flex: 1,
  minWidth: 0,
  border: '1px solid #ced4da',
  borderRadius: 4,
  padding: '4px 7px',
  fontSize: 12,
};

const FileNotesPopupContent = ({ notes, loading, hasMore, onLoadMore }) => {
  const rootRef = React.useRef(null);

  // Notes are user HTML and can embed large images; keep them inside the popup.
  React.useEffect(() => {
    rootRef.current?.querySelectorAll('img').forEach(img => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
    });
  }, [notes]);

  return (
  <div ref={rootRef} style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'hidden' }}>
    {notes.map(note => (
      <div key={note.ID} className="accentTrackingNoteFileNote" style={{ padding: '6px 0', borderBottom: '1px solid #f1f3f5' }}>
        <div style={{ fontSize: 11, color: '#868e96', fontWeight: 600 }}>
          {format.formatDateTime(note.Date, true, true)} {note.FullName}
        </div>
        {/* File notes are trusted internal HTML and can embed images. */}
        <div
          style={{ fontSize: 12, color: '#343a40', overflowWrap: 'anywhere' }}
          dangerouslySetInnerHTML={{ __html: note.Notes }}
        />
      </div>
    ))}
    {!loading && notes.length === 0 && (
      <div style={{ fontSize: 12, color: '#868e96', fontStyle: 'italic', padding: '4px 0' }}>
        {t('No file notes yet.')}
      </div>
    )}
    {loading && <AccentSpinner />}
    {!loading && hasMore && (
      <div className="acc-link" style={{ fontSize: 12, padding: '6px 0', cursor: 'pointer' }} onClick={onLoadMore}>
        {t('Load more')}
      </div>
    )}
  </div>
  );
};

export const FileNotesButton = ({ entityId, entityKey = 'Job', isOpportunity, count }) => {
  const anchor = React.useRef(null);
  const [show, setShow] = React.useState(false);
  const [notes, setNotes] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const [localCount, setLocalCount] = React.useState(count ?? 0);
  const [noteText, setNoteText] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setLocalCount(count ?? 0);
  }, [count]);

  const id = Number.parseInt(entityId, 10);
  const key = isOpportunity ? 'Opportunity' : entityKey;

  const loadPage = React.useCallback(async (skip, replace) => {
    if (!Number.isFinite(id)) return;
    setLoading(true);
    try {
      // Fetch one extra row so we know whether another page exists without a follow-up request.
      const rows = await q_Grid_FileNotes.getAll({ id, key, skip, take: fileNotesPageSize + 1 });
      const fetched = Array.isArray(rows) ? rows : [];
      const page = fetched.slice(0, fileNotesPageSize);
      setNotes(prev => (replace ? page : [...prev, ...page]));
      setHasMore(fetched.length > fileNotesPageSize);
    } finally {
      setLoading(false);
    }
  }, [id, key]);

  const toggle = async () => {
    const next = !show;
    setShow(next);
    if (next) {
      await loadPage(0, true);
    }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text || saving || !Number.isFinite(id)) return;

    setSaving(true);
    try {
      const ok = await update('AddNewJobFileNote', {
        ID: id,
        Notes: text,
        IsOpportunity: !!isOpportunity,
      });
      if (ok) {
        setNoteText('');
        setLocalCount(c => c + 1);
        await loadPage(0, true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-no-drag="true" style={{ marginTop: 8 }}>
      <button
        ref={anchor}
        type="button"
        onClick={() => void toggle()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          border: '1px solid #dee2e6',
          borderRadius: 6,
          background: '#fff',
          padding: '5px 8px',
          fontSize: 12,
          fontWeight: 600,
          color: '#343a40',
          cursor: 'pointer',
        }}
      >
        <span className="material-icons" style={{ fontSize: 13, color: '#868e96' }}>description</span>
        {t('File notes')}
        <Chip tone="gray">{localCount}</Chip>
      </button>
      <AccentPopup
        anchor={anchor}
        show={show}
        onAutoClose={() => setShow(false)}
        minWidth="340px"
        maxWidth="420px"
        headerContent={t('File notes')}
        actionsContent={(
          <div style={{ display: 'flex', gap: 6, width: '100%' }}>
            <input
              style={noteInputStyle}
              placeholder={t('Add a file note..')}
              value={noteText}
              disabled={saving}
              onChange={e => setNoteText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void addNote();
                }
              }}
            />
            <button
              type="button"
              disabled={saving || !noteText.trim()}
              onClick={() => void addNote()}
              style={{
                border: '1px solid #dee2e6',
                borderRadius: 4,
                background: '#fff',
                padding: '3px 10px',
                fontSize: 12,
                fontWeight: 600,
                color: '#1565c0',
                cursor: saving ? 'progress' : 'pointer',
              }}
            >
              {t('Add')}
            </button>
          </div>
        )}
      >
        <FileNotesPopupContent
          notes={notes}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={() => void loadPage(notes.length, false)}
        />
      </AccentPopup>
    </div>
  );
};
